/**
 * Visibility signal for the sticky bottom CTA bar, so other floating UI can
 * keep out of its way.
 *
 * `StickyBottomCta` publishes. This uses the same module-level
 * store-with-listeners pattern as `hooks/use-toast.ts`, which solves the
 * identical cross-tree-without-a-common-ancestor problem elsewhere in this
 * codebase, rather than adding a state-management dependency.
 */

type StickyCtaVisibilityListener = (isVisible: boolean) => void;

const listeners: StickyCtaVisibilityListener[] = [];

let stickyCtaVisible = false;

export const getStickyCtaVisibility = (): boolean => stickyCtaVisible;

export const setStickyCtaVisibility = (isVisible: boolean): void => {
  if (stickyCtaVisible === isVisible) {
    return;
  }

  stickyCtaVisible = isVisible;
  listeners.forEach((listener) => listener(isVisible));
};

export const subscribeToStickyCtaVisibility = (
  listener: StickyCtaVisibilityListener,
): (() => void) => {
  listeners.push(listener);

  return () => {
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
};

/** Test-only: clears both the flag and every subscriber. */
export const resetStickyCtaVisibility = (): void => {
  stickyCtaVisible = false;
  listeners.length = 0;
};
